import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../../hooks/useAuth';
import { useUserLogged } from '../../../hooks/useUserLogged';
import { getErrorMessage, saveBranchToStorage } from '../utils/loginUtils';
import { validationSchema, initialValues, images } from '../constants/loginConstants';

export const useLoginForm = () => {
  const navigate = useNavigate();

  const { login, recoverPassword, isLoading: authLoading, isRecoveringPassword } = useAuth();
  const { setUserLogged } = useUserLogged();

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [isRecoveryDialogOpen, setIsRecoveryDialogOpen] = useState(false);
  const [recoveryMessage, setRecoveryMessage] = useState('');
  const [recoveryError, setRecoveryError] = useState('');

  const handleSubmit = useCallback(
    async (values) => {
      try {
        setErrorMessage('');

        const result = await login({
          email: values.usuario,
          password: values.password,
        });

        if (result && result.status === 200) {
          const { user } = result.data;

          setUserLogged(user);
          setIsLoggedIn(true);
          setBranches(user.branch || []);

          if (user.branch && user.branch.length === 1) {
            const branch = user.branch[0];
            setSelectedBranch(branch);
            saveBranchToStorage(branch);
            setTimeout(() => navigate('/home'), 100);
          }
        }
      } catch (error) {
        const message = getErrorMessage(error);
        setErrorMessage(message);
      }
    },
    [login, setUserLogged, navigate]
  );

  const handleBranchChange = useCallback(
    (event) => {
      const branchId = event.target.value;
      const branch = branches.find(
        (b) => b.id === branchId || b.uuid === branchId || b.uuid_branch === branchId
      );
      setSelectedBranch(branch);
    },
    [branches]
  );

  const handleBranchConfirm = useCallback(() => {
    if (selectedBranch) {
      saveBranchToStorage(selectedBranch);
      navigate('/home');
    }
  }, [selectedBranch, navigate]);

  const clearError = useCallback(() => {
    setErrorMessage('');
  }, []);

  const handleEmailChange = useCallback((value) => {
    setLoginEmail(value);
  }, []);

  const openRecoveryDialog = useCallback(() => {
    setRecoveryError('');
    setRecoveryMessage('');
    setIsRecoveryDialogOpen(true);
  }, []);

  const closeRecoveryDialog = useCallback(() => {
    setIsRecoveryDialogOpen(false);
  }, []);

  const handlePasswordRecovery = useCallback(
    async (email) => {
      try {
        setRecoveryError('');
        const response = await recoverPassword({ email });
        const message =
          response?.message ||
          'Solicitud recibida. Si tu correo está registrado, te enviaremos un enlace para restablecer tu contraseña.';
        setRecoveryMessage(message);
      } catch (error) {
        console.error('Error al solicitar recuperación de contraseña:', error);
        setRecoveryMessage('');
        setRecoveryError(
          'No pudimos procesar tu solicitud. Por favor, intentá nuevamente en unos minutos.'
        );
      }
    },
    [recoverPassword]
  );

  const showBranchSelector = isLoggedIn && branches.length > 1;
  const canProceed = !isLoggedIn || (showBranchSelector ? selectedBranch : true);

  return {
    // Data
    isLoggedIn,
    branches,
    selectedBranch,
    errorMessage,
    authLoading,

    showBranchSelector,
    canProceed,
    recoveryDialogOpen: isRecoveryDialogOpen,
    recoveryMessage,
    recoveryError,
    recoveryDefaultEmail: loginEmail,
    isRecoveringPassword,

    // Config
    validationSchema,
    initialValues,
    images,

    // Handlers
    handleSubmit,
    handleBranchChange,
    handleBranchConfirm,
    clearError,
    handleEmailChange,
    openRecoveryDialog,
    closeRecoveryDialog,
    handlePasswordRecovery,
  };
};
